import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import { useData } from './DataContext';
import FavoritesAPI from '../utils/favoritesAPI';

const FavoritesContext = createContext();

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

export const FavoritesProvider = ({ children }) => {
  const [rawFavorites, setRawFavorites] = useState([]); // Raw favorites from backend
  const [loading, setLoading] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const { products } = useData(); // Get current products from DataContext

  // Merge favorites with current product data
  const favorites = useMemo(() => {
    if (rawFavorites.length === 0) return [];
    if (products.length === 0) return rawFavorites;

    return rawFavorites.map(fav => {
      // Find current product data
      const currentProduct = products.find(p => String(p.id) === String(fav.id));
      if (currentProduct) {
        // Merge with current product data (price, image, etc.)
        return {
          id: fav.id,
          name: currentProduct.name || fav.name,
          price: currentProduct.price || fav.price,
          image: currentProduct.image || fav.image,
          rating: currentProduct.rating,
          reviews: currentProduct.reviews,
          category: currentProduct.category
        };
      }
      return fav; // Keep original if product not found
    });
  }, [rawFavorites, products]);

  const loadFavorites = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const data = await FavoritesAPI.getFavorites();
      // Normalize backend format to frontend format
      const normalizedFavorites = (data.favorites || []).map(item => ({
        id: item.product_id,
        name: item.product_name,
        price: item.product_price,
        image: item.product_image
      }));
      setRawFavorites(normalizedFavorites);
    } catch (error) {
      console.error('Failed to load favorites:', error);
      if (error.message === 'NOT_AUTHENTICATED') {
        setRawFavorites([]);
      }
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Load favorites from backend when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      loadFavorites();
    } else {
      setRawFavorites([]);
    }
  }, [isAuthenticated, loadFavorites, user?.id]);

  const addToFavorites = async (product) => {
    // Check authentication
    if (!isAuthenticated) {
      toast.info('Lütfen giriş yapın');
      const currentPath = window.location.pathname;
      setTimeout(() => {
        window.location.href = `/giris?returnUrl=${encodeURIComponent(currentPath)}`;
      }, 500);
      return;
    }

    try {
      setLoading(true);
      await FavoritesAPI.addFavorite(product);
      setRawFavorites((prev) => {
        const pid = String(product?.id);
        if (!pid) return prev;
        if (prev.some((x) => String(x.id) === pid)) return prev;
        return [{ id: pid }, ...prev];
      });
      toast.success('Ürün favorilere eklendi');
    } catch (error) {
      console.error('Failed to add to favorites:', error);
      if (error.message === 'NOT_AUTHENTICATED') {
        toast.error('Lütfen giriş yapın');
        window.location.href = '/giris';
      } else {
        toast.error('Favorilere eklenirken hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  const removeFromFavorites = async (productId) => {
    if (!isAuthenticated) {
      toast.info('Lütfen giriş yapın');
      window.location.href = '/giris';
      return;
    }

    try {
      setLoading(true);
      await FavoritesAPI.removeFavorite(productId);
      setRawFavorites((prev) => prev.filter((x) => String(x.id) !== String(productId)));
      toast.success('Ürün favorilerden çıkarıldı');
    } catch (error) {
      console.error('Failed to remove from favorites:', error);
      toast.error('Favorilerden çıkarılırken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async (product) => {
    if (!isAuthenticated) {
      toast.info('Lütfen giriş yapın');
      const currentPath = window.location.pathname;
      setTimeout(() => {
        window.location.href = `/giris?returnUrl=${encodeURIComponent(currentPath)}`;
      }, 500);
      return;
    }

    if (isFavorite(product.id)) {
      await removeFromFavorites(product.id);
    } else {
      await addToFavorites(product);
    }
  };

  const isFavorite = (productId) => {
    return favorites.some((item) => item.id === String(productId));
  };

  const clearFavorites = async () => {
    if (!isAuthenticated) {
      toast.info('Lütfen giriş yapın');
      window.location.href = '/giris';
      return;
    }

    try {
      setLoading(true);
      await FavoritesAPI.clearFavorites();
      setRawFavorites([]);
      toast.success('Favoriler temizlendi');
    } catch (error) {
      console.error('Failed to clear favorites:', error);
      toast.error('Favoriler temizlenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <FavoritesContext.Provider
      value={{
        favorites,
        loading,
        addToFavorites,
        removeFromFavorites,
        toggleFavorite,
        isFavorite,
        clearFavorites,
      }}
    >
      {children}
    </FavoritesContext.Provider>
  );
};
